import { useState, useEffect, useCallback } from 'react'
import { VideoService, type WatchHistoryItem } from '../services/videos'
import { AuthService, type AuthUser } from '../services/auth'
import { supabase } from '../lib/supabase'
import { useAdminSelectedSchool } from '../utils/adminSchoolContext'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import PasswordChangeDialog from '../components/PasswordChangeDialog'
import SchoolSettingsTabContent from '../components/teacher/SchoolSettingsTabContent'
import PortfolioTabContent from '../components/PortfolioTabContent'
import VideoThumbnail from '../components/VideoThumbnail'
import hashtagIcon from '../assets/icons/hashtag-solid.png'
import calendarIcon from '../assets/icons/calendar-solid.png'
import clockIcon from '../assets/icons/clock-solid.png'

type TabType = 'portfolio' | 'watch-history' | 'settings' | 'school-settings'

const TAB_TYPES: TabType[] = ['portfolio', 'watch-history', 'settings', 'school-settings']

const isTabType = (value: string | null): value is TabType =>
  value !== null && (TAB_TYPES as string[]).includes(value)

const MyPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isTeacherMode = searchParams.get('teacherMode') === 'true'
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  // Pick the tab from the URL up front so the first render already shows it
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const tabParam = searchParams.get('tab')
    if (isTabType(tabParam)) return tabParam
    return isTeacherMode ? 'school-settings' : 'portfolio'
  })
  const [schoolName, setSchoolName] = useState<string>('')
  const [watchHistory, setWatchHistory] = useState<WatchHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false)

  // Watch history pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalHistoryCount, setTotalHistoryCount] = useState(0)
  const itemsPerPage = 10

  // Settings form state
  const [profileName, setProfileName] = useState<string>('')
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string>('')

  const adminSelectedSchool = useAdminSelectedSchool()

  // Helper function to safely render tags (copied from VideosPage)
  const renderTags = (tags: unknown, maxTags: number = 3, sizeClass: string = "text-xs") => {
    // Type guard to ensure tags is a proper array of tag objects
    if (!Array.isArray(tags) || tags.length === 0) {
      return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
    }
    // Filter and validate tag objects
    const validTags = tags
      .filter((tag): tag is { name: string; slug: string } =>
        tag &&
        typeof tag === 'object' &&
        'name' in tag &&
        'slug' in tag &&
        typeof tag.name === 'string' &&
        typeof tag.slug === 'string'
      )
      .slice(0, maxTags)
    if (validTags.length === 0) {
      return <span className={`${sizeClass} text-gray-400`}>タグなし</span>
    }
    // Determine icon size and padding based on text size
    const isSmall = sizeClass === "text-xs"
    const iconClass = isSmall ? "w-3 h-3" : "w-4 h-4"
    const paddingClass = isSmall ? "px-1 py-0.5" : "px-2 py-1"
    return validTags.map(tag => (
      <Link
        key={tag.slug}
        to={`/tags/${encodeURIComponent(tag.slug)}`}
        className={`flex items-center gap-1 ${sizeClass} hover:bg-gray-100 rounded ${paddingClass} transition-colors flex-shrink-0`}
        onClick={(e) => e.stopPropagation()}
      >
        <img src={hashtagIcon} alt="#" className={iconClass} />
        <span className="font-semibold text-gray-800 whitespace-nowrap truncate max-w-20">{tag.name}</span>
      </Link>
    ))
  }

  const loadSchoolData = async (schoolId: string) => {
    try {
      const { data, error } = await supabase
        .from('schools')
        .select('name')
        .eq('id', schoolId)
        .single()

      if (error) {
        console.error('Failed to load school data:', error)
        return
      }

      if (data) {
        setSchoolName(data.name)
      }
    } catch (error) {
      console.error('Failed to load school data:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await AuthService.signOut()
      navigate('/')
      window.scrollTo(0, 0)
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const loadUserData = useCallback(async () => {
    try {
      setIsLoading(true)
      const user = await AuthService.getCurrentUser()
      if (user) {
        setCurrentUser(user)
        setProfileName(user.nickname || '')
        if (user.school_id) {
          await loadSchoolData(user.school_id)
        }
      } else {
        setError('ユーザー情報の取得に失敗しました')
      }
    } catch (error) {
      console.error('Failed to load user data:', error)
      setError('ユーザー情報の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleSaveProfile = async () => {
    if (!currentUser) return

    try {
      setIsSavingProfile(true)
      setSaveMessage('')

      await AuthService.updateUser(currentUser.id, {
        nickname: profileName.trim()
      })

      // Update local state
      setCurrentUser(prev => prev ? { ...prev, nickname: profileName.trim() } : null)
      setSaveMessage('プロフィールが正常に更新されました')

      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(''), 3000)
    } catch (error) {
      console.error('Failed to update profile:', error)
      setSaveMessage('プロフィールの更新に失敗しました')

      // Clear error message after 5 seconds
      setTimeout(() => setSaveMessage(''), 5000)
    } finally {
      setIsSavingProfile(false)
    }
  }

  const loadWatchHistory = useCallback(async (page: number = currentPage) => {
    if (!currentUser) return

    try {
      const offset = (page - 1) * itemsPerPage
      const [history, totalCount] = await Promise.all([
        VideoService.getWatchHistory(currentUser.id, itemsPerPage, offset),
        VideoService.getWatchHistoryCount(currentUser.id)
      ])
      setWatchHistory(history)
      setTotalHistoryCount(totalCount)
    } catch (error) {
      console.error('Failed to load watch history:', error)
      setError('視聴履歴の取得に失敗しました')
    }
  }, [currentUser, currentPage, itemsPerPage])

  // Pagination handlers
  const totalPages = Math.ceil(totalHistoryCount / itemsPerPage)

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
      loadWatchHistory(newPage)
    }
  }

  const handlePrevPage = () => {
    handlePageChange(currentPage - 1)
  }

  const handleNextPage = () => {
    handlePageChange(currentPage + 1)
  }

  useEffect(() => {
    loadUserData()
  }, [loadUserData])

  // Set default tab based on URL param
  useEffect(() => {
    if (currentUser) {
      const tabParam = searchParams.get('tab')
      const teacherMode = searchParams.get('teacherMode') === 'true'
      if (isTabType(tabParam)) {
        setActiveTab(tabParam)
      } else if (teacherMode && ['teacher', 'admin', 'super_admin'].includes(currentUser.role)) {
        setActiveTab('school-settings')
      } else {
        setActiveTab('portfolio')
      }
    }
  }, [currentUser, searchParams])

  useEffect(() => {
    if (currentUser && activeTab === 'watch-history') {
      setCurrentPage(1)
      loadWatchHistory(1)
    }
  }, [activeTab, currentUser, loadWatchHistory])

  // Helper function to format date and time (same as VideosPage)
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    const formattedDate = date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    const formattedTime = date.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    })

    return { date: formattedDate, time: formattedTime }
  }


  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )
    }

    switch (activeTab) {
      case 'portfolio':
        return currentUser
          ? <PortfolioTabContent userId={currentUser.id} />
          : null
      case 'watch-history':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">動画視聴履歴</h2>
              <span className="text-sm text-gray-500">{totalHistoryCount}件の履歴</span>
            </div>

            {watchHistory.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg mb-4">📺</div>
                <p className="text-gray-600 mb-4">まだ視聴した動画がありません</p>
                <Link
                  to="/videos"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  動画を見る
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {watchHistory.map((item) =>
                  item.video ? (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg border border-gray-100 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex p-4 h-32">
                        {/* Clickable area for thumbnail and title */}
                        <Link
                          to={`/video/${item.video.id}`}
                          className="flex flex-1"
                        >
                          {/* Thumbnail */}
                          <div className="w-48 flex-shrink-0 mr-4 rounded-lg overflow-hidden">
                            <VideoThumbnail
                              thumbnailUrl={item.video.thumbnail_url}
                              title={item.video.title}
                              className="w-full aspect-video"
                              thumbnailTitle={item.video.thumbnail_title}
                              schoolOriginal={item.video.is_school_video && item.video.video_type === 'chat_only'}
                            />
                          </div>

                          {/* Video Info - Vertical layout */}
                          <div className="flex-1 min-w-0 flex flex-col justify-start space-y-3">
                            {/* Title */}
                            <h3 className="font-bold line-clamp-2 text-sm leading-relaxed text-theme-primary">
                              {item.video.title}
                            </h3>

                            {/* Tags */}
                            {!item.video.is_school_video && (
                              <div className="flex gap-2 overflow-hidden">
                                {renderTags(item.video.tags, 3, "text-xs")}
                              </div>
                            )}

                            {/* Date and Duration Info */}
                            <div className="text-xs text-gray-500">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                  <img src={calendarIcon} alt="" className="w-3 h-3 opacity-60" />
                                  <span>
                                    {formatDateTime(item.watched_at).date}
                                  </span>
                                  <img src={clockIcon} alt="" className="w-3 h-3 opacity-60" />
                                  <span>
                                    {formatDateTime(item.watched_at).time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  ) : null
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center space-x-4 py-6">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage <= 1}
                      className={`px-3 py-1 text-sm transition-colors duration-200 ${
                        currentPage <= 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:text-gray-700 cursor-pointer'
                      }`}
                    >
                      ← 前のページ
                    </button>
                    <span className="text-sm text-gray-600">{currentPage} / {totalPages}</span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages}
                      className={`px-4 py-2 text-sm rounded transition-colors duration-200 ${
                        currentPage >= totalPages
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-red-500 text-white hover:bg-red-600 cursor-pointer'
                      }`}
                    >
                      次のページ →
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )

      case 'settings':
        return (
          <div className="space-y-6">
            <div className="flex gap-6">
              {/* Left Side - Profile Display */}
              <div className="w-80 flex-shrink-0">
                <div className="bg-white rounded-lg shadow p-6">

                  {/* Profile Image */}
                  <div className="flex justify-center mb-6">
                    <div className="w-24 h-24 bg-gray-200 rounded-full overflow-hidden flex items-center justify-center">
                      <div className="w-12 h-12 text-gray-600">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                      {currentUser?.name}さん
                    </h2>
                  </div>

                  {/* School Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-700">
                        {schoolName} {currentUser?.grade} {currentUser?.class}
                      </p>
                      {currentUser?.attendance_number && (
                        <p className="text-gray-700">出席番号：{currentUser.attendance_number}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side - Editable Fields and Actions */}
              <div className="flex-1 space-y-6">
                {/* Editable Fields */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">プロフィール設定</h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ニックネーム</label>
                      <input
                        type="text"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="ニックネームを入力"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">メールアドレス</label>
                      <input
                        type="email"
                        value={currentUser?.email || ''}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                      />
                    </div>
                  </div>

                  {/* Save Message */}
                  {saveMessage && (
                    <div className={`mt-4 p-3 rounded-md text-sm ${
                      saveMessage.includes('失敗')
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : 'bg-green-50 text-green-700 border border-green-200'
                    }`}>
                      {saveMessage}
                    </div>
                  )}

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex space-x-3">
                      <button
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile || !profileName.trim()}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                          isSavingProfile || !profileName.trim()
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'text-white bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {isSavingProfile ? '保存中...' : '変更を保存'}
                      </button>
                      <button
                        onClick={() => setIsPasswordChangeOpen(true)}
                        className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors duration-200"
                      >
                        パスワードを変更
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">その他</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors duration-200"
                    >
                      ログアウト
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'school-settings':
        if (currentUser?.role === 'teacher' && currentUser.school_id) {
          return <SchoolSettingsTabContent schoolId={currentUser.school_id} />
        }
        if (currentUser?.role === 'admin' || currentUser?.role === 'super_admin') {
          if (!adminSelectedSchool) {
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                <h3 className="text-lg font-medium text-blue-900">
                  画面上部のボタンから学校を選択してください
                </h3>
              </div>
            )
          }
          return <SchoolSettingsTabContent schoolId={adminSelectedSchool.id} />
        }
        return null

      default:
        return null
    }
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-red-800 mb-2">エラーが発生しました</h2>
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex" aria-label="Tabs">
                {currentUser && isTeacherMode && ['teacher', 'admin', 'super_admin'].includes(currentUser.role) && (
                  <button
                    onClick={() => setActiveTab('school-settings')}
                    className={`flex-1 py-4 px-6 first:rounded-tl-lg last:rounded-tr-lg border-b-2 font-medium text-sm transition-colors duration-200 text-center ${
                      activeTab === 'school-settings'
                        ? 'border-purple-500 text-purple-600 bg-purple-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    学校設定
                  </button>
                )}
                {!isTeacherMode && (
                  <button
                    onClick={() => setActiveTab('portfolio')}
                    className={`flex-1 py-4 px-6 first:rounded-tl-lg last:rounded-tr-lg border-b-2 font-medium text-sm transition-colors duration-200 text-center ${
                      activeTab === 'portfolio'
                        ? 'border-[#da4574] text-[#da4574] bg-[#fff0f4]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    キャリアサマリ
                  </button>
                )}
                {!isTeacherMode && (
                  <button
                    onClick={() => setActiveTab('watch-history')}
                    className={`flex-1 py-4 px-6 first:rounded-tl-lg last:rounded-tr-lg border-b-2 font-medium text-sm transition-colors duration-200 text-center ${
                      activeTab === 'watch-history'
                        ? 'border-[#ebad47] text-[#ebad47] bg-[#fdf8f0]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    動画視聴履歴
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`flex-1 py-4 px-6 first:rounded-tl-lg last:rounded-tr-lg border-b-2 font-medium text-sm transition-colors duration-200 text-center ${
                    activeTab === 'settings'
                      ? 'border-[#65c1c8] text-[#65c1c8] bg-[#e9f9fa]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  プロフィール設定
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>

      {/* Password Change Dialog */}
      <PasswordChangeDialog
        isOpen={isPasswordChangeOpen}
        onSuccess={() => setIsPasswordChangeOpen(false)}
        onError={(error) => console.error('Password change error:', error)}
        onClose={() => setIsPasswordChangeOpen(false)}
      />
    </div>
  )
}

export default MyPage